import { useState } from "react";
import { Plus, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useDraft } from "@/components/itsm-config/draft-context";
import { MondayColumnImport } from "@/components/itsm-config/provider-field-import";
import { PriorityMappingEditor } from "@/components/itsm-config/mapping-editors";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { useGeneralMappingSave } from "@/hooks/use-general-mapping-save";
import { useHarmonyFieldLabels } from "@/hooks/use-harmony-field-labels";
import { useSourceCatalogItem } from "@/hooks/use-intents";
import { useMondayColumns, usePmScopeName } from "@/hooks/use-pm-metadata";
import { decodeField, encodeField } from "@/lib/itsm-config/field-mapping";
import { findRule } from "@/lib/itsm-config/general-mapping";
import {
  ITEM_NAME_TARGET,
  MONDAY_BUILTIN_FIELDS,
  NAME_TARGET_BUILTINS,
  boardPairings,
  claimedByConfig,
  fieldIdentity,
  mondayBoards,
  patchBoardSync,
  type MondayPairing,
  type PairingSync,
} from "@/lib/itsm-config/monday";
import {
  HARMONY_PRIORITIES,
  HARMONY_PRIORITY_LABELS,
  type GeneralMatch,
  type MondayBoardSync,
  type MondayFieldMapping,
  type MondayHarmonyField,
  type MondaySyncDirection,
  type ProviderConfig,
} from "@/lib/types/itsm-config";

interface Props {
  tenantId: string;
  credentialId: string;
  providerConfig: ProviderConfig;
}

const DIRECTIONS: { value: MondaySyncDirection; label: string }[] = [
  { value: "both", label: "Both ways" },
  { value: "outbound", label: "Harmony → monday" },
  { value: "inbound", label: "monday → Harmony" },
];

const DIRECTION_LABELS: Record<PairingSync, string> = {
  ...(Object.fromEntries(DIRECTIONS.map((d) => [d.value, d.label])) as Record<
    MondaySyncDirection,
    string
  >),
  create: "At creation only",
};

const SOURCE_NOTES: Record<MondayPairing["source"], string> = {
  explicit: "",
  legacy: "set under Two-way sync / Custom fields",
  default: "default",
};

// Column types a value can be written to: monday_values' writable types plus
// people, which assignee and reporter resolve by email.
const MAPPABLE_COLUMN_TYPES = new Set([
  "text",
  "long_text",
  "email",
  "link",
  "numbers",
  "date",
  "status",
  "dropdown",
  "checkbox",
  "people",
  "person",
]);

const PEOPLE_BUILTINS = new Set(["assignee", "reporter"]);
const PEOPLE_COLUMN_TYPES = new Set(["people", "person"]);

function useBoardMappingsPatch() {
  const { update } = useDraft();
  return (boardId: string, mappings: MondayFieldMapping[]) => {
    update((current) =>
      patchBoardSync(current, boardId, {
        field_mappings: mappings.length ? mappings : null,
      }),
    );
  };
}

/** Priority labels are resolved per board, as status labels are. */
function boardMatch(boardId: string): GeneralMatch {
  return { integration_id: null, provider_scope: boardId, issue_type: null };
}

function BoardPriorityMapping({
  boardId,
  labels,
  providerConfig,
}: {
  boardId: string;
  labels: string[];
  providerConfig: ProviderConfig;
}) {
  const save = useGeneralMappingSave();
  const match = boardMatch(boardId);
  return (
    <div className="space-y-1 pt-2">
      <p className="text-xs font-medium">Priority translation</p>
      <PriorityMappingEditor
        value={
          findRule(providerConfig.general_mapping, match)?.priority_mapping ??
          {}
        }
        onChange={(m) => {
          save(match, { priority_mapping: Object.keys(m).length ? m : null });
        }}
        harmonyOptions={HARMONY_PRIORITIES}
        harmonyLabels={HARMONY_PRIORITY_LABELS}
        providerLabel="monday"
        externalOptions={labels.map((l) => ({ value: l, label: l }))}
        bidirectional
      />
    </div>
  );
}

function BoardFieldMappings({
  tenantId,
  credentialId,
  boardId,
  deskIds,
  sync,
  providerConfig,
}: {
  tenantId: string;
  credentialId: string;
  boardId: string;
  deskIds: string[];
  sync: MondayBoardSync | undefined;
  providerConfig: ProviderConfig;
}) {
  const boardName = usePmScopeName("monday", tenantId, boardId);
  const columns = useMondayColumns(tenantId, boardId);
  const patch = useBoardMappingsPatch();
  const { fields, items, label } = useHarmonyFieldLabels(tenantId, deskIds);
  const [catalogItemId, setCatalogItemId] = useState("");
  const catalogItem = useSourceCatalogItem(tenantId, catalogItemId);
  const [newField, setNewField] = useState("");
  const [newTarget, setNewTarget] = useState("");
  const [newDirection, setNewDirection] = useState<MondaySyncDirection>("both");

  const explicit = sync?.field_mappings ?? [];
  const { pairings } = boardPairings(sync);
  const claimed = claimedByConfig(sync);
  const columnList = columns.data?.options ?? [];
  const columnName = (target: string) =>
    target === ITEM_NAME_TARGET
      ? "Item name"
      : (columnList.find((c) => c.value === target)?.label ?? target);

  const fieldOptions = [
    ...MONDAY_BUILTIN_FIELDS.map((key) => ({
      field: { kind: "builtin", key } as MondayHarmonyField,
    })),
    ...fields.definitions.map((d) => ({
      field: { kind: "custom", key: d.id } as MondayHarmonyField,
    })),
    ...(catalogItemId
      ? (catalogItem.data?.form_fields ?? [])
          .filter((f) => f.label)
          .map((f) => ({
            field: {
              kind: "catalog",
              key: String(f.label),
              catalog_item_id: catalogItemId,
            } as MondayHarmonyField,
          }))
      : []),
  ]
    .filter(({ field }) => !claimed.fields.has(fieldIdentity(field)))
    .map(({ field }) => ({ value: encodeField(field), label: label(field) }));

  const chosen = newField ? decodeField(newField) : null;
  const targetOptions = [
    ...(chosen?.kind === "builtin" && !NAME_TARGET_BUILTINS.has(chosen.key)
      ? []
      : [{ value: ITEM_NAME_TARGET, label: "Item name" }]),
    ...columnList
      .filter((c) => MAPPABLE_COLUMN_TYPES.has(c.column_type))
      .filter(
        (c) =>
          !PEOPLE_COLUMN_TYPES.has(c.column_type) ||
          (chosen?.kind === "builtin" && PEOPLE_BUILTINS.has(chosen.key)),
      )
      .map((c) => ({ value: c.value, label: `${c.label} (${c.column_type})` })),
  ].filter((o) => !claimed.targets.has(o.value));

  const persist = (next: MondayFieldMapping[]) => {
    patch(boardId, next);
  };
  const priority = pairings.find(
    (p) => p.harmony.kind === "builtin" && p.harmony.key === "priority",
  );
  const priorityLabels =
    columnList.find((c) => c.value === priority?.target)?.labels ?? [];

  return (
    <div className="space-y-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-medium">{boardName || boardId}</p>
        <MondayColumnImport
          tenantId={tenantId}
          credentialId={credentialId}
          boardId={boardId}
        />
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[260px]">Harmony field</TableHead>
            <TableHead>monday</TableHead>
            <TableHead className="w-[170px]">Direction</TableHead>
            <TableHead className="w-10" />
          </TableRow>
        </TableHeader>
        <TableBody>
          {pairings.map((p) => (
            <TableRow key={`${fieldIdentity(p.harmony)}|${p.target}`}>
              <TableCell className="text-sm">
                {label(p.harmony)}
                {p.source !== "explicit" && (
                  <Badge variant="outline" className="ml-2 text-xs">
                    {SOURCE_NOTES[p.source]}
                  </Badge>
                )}
              </TableCell>
              <TableCell className="text-sm">{columnName(p.target)}</TableCell>
              <TableCell>
                {p.source === "explicit" &&
                p.index !== undefined &&
                p.direction !== "create" ? (
                  <MetadataSelect
                    options={DIRECTIONS}
                    value={p.direction}
                    onChange={(v) => {
                      persist(
                        explicit.map((m, i) =>
                          i === p.index
                            ? { ...m, direction: v as MondaySyncDirection }
                            : m,
                        ),
                      );
                    }}
                    allowCustom={false}
                  />
                ) : (
                  <span className="text-muted-foreground text-sm">
                    {DIRECTION_LABELS[p.direction]}
                  </span>
                )}
              </TableCell>
              <TableCell>
                {p.source === "explicit" && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => {
                      persist(explicit.filter((_, i) => i !== p.index));
                    }}
                  >
                    <X className="h-4 w-4" />
                    <span className="sr-only">Remove</span>
                  </Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-[200px]">
          <MetadataSelect
            options={items.map((i) => ({ value: i.id, label: i.name }))}
            value={catalogItemId}
            onChange={(v) => {
              setCatalogItemId(v);
              setNewField("");
            }}
            placeholder="Catalog item (optional)"
            allowCustom={false}
          />
        </div>
        <div className="w-[240px]">
          <MetadataSelect
            options={fieldOptions}
            isLoading={fields.isLoading || catalogItem.isLoading}
            value={newField}
            onChange={(v) => {
              setNewField(v);
              setNewTarget("");
            }}
            placeholder="Harmony field..."
            allowCustom={false}
          />
        </div>
        <div className="w-[220px]">
          <MetadataSelect
            options={targetOptions}
            isLoading={columns.isLoading}
            value={newTarget}
            onChange={setNewTarget}
            placeholder="...to a column"
            allowCustom={false}
            disabled={!newField}
          />
        </div>
        <div className="w-[170px]">
          <MetadataSelect
            options={DIRECTIONS}
            value={newDirection}
            onChange={(v) => {
              setNewDirection(v as MondaySyncDirection);
            }}
            allowCustom={false}
          />
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={!chosen || !newTarget}
          onClick={() => {
            if (!chosen) return;
            persist([
              ...explicit,
              { harmony: chosen, target: newTarget, direction: newDirection },
            ]);
            setNewField("");
            setNewTarget("");
          }}
        >
          <Plus className="mr-1 h-4 w-4" />
          Add
        </Button>
      </div>
      {priority && (
        <BoardPriorityMapping
          boardId={boardId}
          labels={priorityLabels}
          providerConfig={providerConfig}
        />
      )}
    </div>
  );
}

/** monday: every Harmony field each board keeps in step, and which way. */
export function MondayFieldMappingsSection({
  tenantId,
  credentialId,
  providerConfig,
}: Props) {
  const boards = mondayBoards(providerConfig);
  const deskIds = [
    ...new Set(
      (providerConfig.export_mapping ?? [])
        .map((r) => r.match.desk_id)
        .filter((d): d is string => Boolean(d)),
    ),
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Field mappings</CardTitle>
        <CardDescription>
          Pair ticket fields - built-ins, desk custom fields and catalog form
          fields - with a board&apos;s columns or the item name, and choose
          which way each syncs. Each field and each column pairs once: mapping
          title elsewhere frees the item name, and a field mapped onto the item
          name replaces the title there. Nothing comes back to the ticket unless
          its pairing says so. Needs two-way sync on.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {boards.length === 0 ? (
          <p className="text-muted-foreground py-4 text-center text-sm">
            No boards yet. Add an export rule under &quot;Where tickets go&quot;
            first.
          </p>
        ) : (
          boards.map((boardId) => (
            <BoardFieldMappings
              key={boardId}
              tenantId={tenantId}
              credentialId={credentialId}
              boardId={boardId}
              deskIds={deskIds}
              // eslint-disable-next-line security/detect-object-injection -- boardId comes from our own config
              sync={providerConfig.board_sync?.[boardId]}
              providerConfig={providerConfig}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}
